// Comprehensive error handling system

class ErrorHandler {
    constructor() {
        this.errorLog = [];
        this.maxLogSize = 100;
        this.setupGlobalHandlers();
        this.errorReporting = {
            enabled: false, // Set to true in production
            endpoint: '/api/errors'
        };
    }
    
    // Error types
    ErrorTypes = {
        NETWORK: 'NETWORK_ERROR',
        AUTH: 'AUTH_ERROR',
        VALIDATION: 'VALIDATION_ERROR',
        PERMISSION: 'PERMISSION_ERROR',
        RATE_LIMIT: 'RATE_LIMIT_ERROR',
        DATABASE: 'DATABASE_ERROR',
        UNKNOWN: 'UNKNOWN_ERROR'
    };
    
    // User-friendly error messages
    errorMessages = {
        // Network errors
        'Failed to fetch': 'Network connection error. Please check your internet connection.',
        'NetworkError': 'Unable to connect to the server. Please try again.',
        
        // Firebase Auth errors
        'auth/email-already-in-use': 'This email is already registered. Please sign in instead.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/weak-password': 'Password should be at least 8 characters long.',
        'auth/user-not-found': 'No account found with this email. Please sign up first.',
        'auth/wrong-password': 'Incorrect password. Please try again.',
        'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
        'auth/network-request-failed': 'Network error. Please check your connection.',
        
        // Firestore errors
        'permission-denied': 'You don\'t have permission to perform this action.',
        'not-found': 'The requested resource was not found.',
        'already-exists': 'This resource already exists.',
        'resource-exhausted': 'Too many requests. Please slow down.',
        'unavailable': 'Service temporarily unavailable. Please try again.',
        'invalid-argument': 'Invalid data provided. Please check your input.',
        'failed-precondition': 'Operation failed. Please ensure all requirements are met.',
        'aborted': 'Operation was cancelled. Please try again.',
        'out-of-range': 'Value out of acceptable range.',
        'unimplemented': 'This feature is not yet available.',
        'internal': 'An internal error occurred. Please try again later.',
        'data-loss': 'Data consistency error. Please contact support.',
        'unauthenticated': 'Please sign in to continue.',
        
        // Custom errors
        'RATE_LIMIT_ERROR': 'You\'re doing that too quickly. Please wait a moment.',
        'VALIDATION_ERROR': 'Please check your input and try again.',
        'SESSION_EXPIRED': 'Your session has expired. Please sign in again.'
    };
    
    // Setup global error handlers
    setupGlobalHandlers() {
        // Handle unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            console.error('Unhandled promise rejection:', event.reason);
            this.handleError(event.reason, 'PROMISE_REJECTION');
            event.preventDefault();
        });
        
        // Handle global errors
        window.addEventListener('error', (event) => {
            console.error('Global error:', event.error);
            this.handleError(event.error, 'GLOBAL_ERROR');
            event.preventDefault();
        });
    }
    
    // Main error handling method
    handleError(error, context = '', showToUser = true) {
        // Log the error
        this.logError(error, context);
        
        // Determine error type
        const errorType = this.categorizeError(error);
        
        // Get user-friendly message
        const userMessage = this.getUserMessage(error);
        
        // Show to user if requested
        if (showToUser) {
            this.showErrorToUser(userMessage, errorType);
        }
        
        // Report to analytics
        this.reportToAnalytics(error, errorType, context);
        
        // Report to server in production
        if (this.errorReporting.enabled) {
            this.reportToServer(error, errorType, context);
        }
        
        return {
            type: errorType,
            message: userMessage,
            originalError: error
        };
    }
    
    // Categorize error type
    categorizeError(error) {
        if (!error) return this.ErrorTypes.UNKNOWN;
        
        const errorString = error.toString().toLowerCase();
        const errorCode = error.code ? error.code.toLowerCase() : '';
        
        if (errorString.includes('network') || errorString.includes('fetch')) {
            return this.ErrorTypes.NETWORK;
        }
        
        if (errorCode.includes('auth/') || errorString.includes('auth')) {
            return this.ErrorTypes.AUTH;
        }
        
        if (errorCode === 'permission-denied') {
            return this.ErrorTypes.PERMISSION;
        }
        
        if (errorString.includes('rate') || errorString.includes('limit')) {
            return this.ErrorTypes.RATE_LIMIT;
        }
        
        if (errorString.includes('validation') || errorString.includes('invalid')) {
            return this.ErrorTypes.VALIDATION;
        }
        
        if (errorCode.includes('firestore') || errorString.includes('database')) {
            return this.ErrorTypes.DATABASE;
        }
        
        return this.ErrorTypes.UNKNOWN;
    }
    
    // Get user-friendly message
    getUserMessage(error) {
        if (!error) return 'An unexpected error occurred. Please try again.';
        
        // Check for specific error code
        if (error.code && this.errorMessages[error.code]) {
            return this.errorMessages[error.code];
        }
        
        // Check error message
        const errorMessage = error.message || error.toString();
        for (const [key, value] of Object.entries(this.errorMessages)) {
            if (errorMessage.toLowerCase().includes(key.toLowerCase())) {
                return value;
            }
        }
        
        // Check error type
        if (error.type && this.errorMessages[error.type]) {
            return this.errorMessages[error.type];
        }
        
        // Default message based on error type
        const errorType = this.categorizeError(error);
        switch (errorType) {
        case this.ErrorTypes.NETWORK:
            return 'Connection error. Please check your internet and try again.';
        case this.ErrorTypes.AUTH:
            return 'Authentication error. Please try signing in again.';
        case this.ErrorTypes.PERMISSION:
            return 'You don\'t have permission to do that.';
        case this.ErrorTypes.RATE_LIMIT:
            return 'Too many requests. Please wait a moment.';
        case this.ErrorTypes.VALIDATION:
            return 'Please check your input and try again.';
        default:
            return 'Something went wrong. Please try again.';
        }
    }
    
    // Show error to user
    showErrorToUser(message, type) {
        // Remove any existing error notifications
        const existingError = document.getElementById('error-notification');
        if (existingError) {
            existingError.remove();
        }
        
        // Create error notification
        const notification = document.createElement('div');
        notification.id = 'error-notification';
        notification.className = 'error-notification';
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #ef4444;
            color: white;
            padding: 16px 24px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            z-index: 10001;
            max-width: 400px;
            animation: slideIn 0.3s ease;
        `;
        
        notification.innerHTML = `
            <div style="display: flex; align-items: start; gap: 12px;">
                <span style="font-size: 20px;">⚠️</span>
                <div style="flex: 1;">
                    <div style="font-weight: 600; margin-bottom: 4px;">Error</div>
                    <div style="font-size: 14px;">${message}</div>
                </div>
                <button onclick="this.parentElement.parentElement.remove()" 
                        style="background: none; border: none; color: white; font-size: 20px; cursor: pointer; padding: 0; margin-left: 12px;">
                    ×
                </button>
            </div>
        `;
        
        document.body.appendChild(notification);
        
        // Auto-remove after 5 seconds
        setTimeout(() => {
            if (document.getElementById('error-notification')) {
                notification.style.animation = 'slideOut 0.3s ease';
                setTimeout(() => notification.remove(), 300);
            }
        }, 5000);
    }
    
    // Log error
    logError(error, context) {
        const errorEntry = {
            timestamp: new Date().toISOString(),
            error: {
                message: error?.message || error?.toString() || 'Unknown error',
                code: error?.code,
                stack: error?.stack
            },
            context,
            userAgent: navigator.userAgent,
            url: window.location.href
        };
        
        this.errorLog.push(errorEntry);
        
        // Keep log size limited
        if (this.errorLog.length > this.maxLogSize) {
            this.errorLog.shift();
        }
        
        // Store in sessionStorage for debugging
        try {
            sessionStorage.setItem('errorLog', JSON.stringify(this.errorLog));
        } catch (e) {
            console.error('Failed to store error log:', e);
        }
    }
    
    // Report to analytics
    reportToAnalytics(error, errorType, context) {
        if (typeof firebase !== 'undefined' && firebase.analytics) {
            try {
                firebase.analytics().logEvent('error', {
                    error_type: errorType,
                    error_message: error?.message || 'Unknown',
                    error_context: context,
                    error_code: error?.code
                });
            } catch (e) {
                console.error('Failed to report to analytics:', e);
            }
        }
    }
    
    // Report to server
    async reportToServer(error, errorType, context) {
        try {
            await fetch(this.errorReporting.endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    timestamp: new Date().toISOString(),
                    errorType,
                    error: {
                        message: error?.message,
                        code: error?.code,
                        stack: error?.stack
                    },
                    context,
                    userAgent: navigator.userAgent,
                    url: window.location.href,
                    userId: window.currentUser?.uid
                })
            });
        } catch (e) {
            console.error('Failed to report error to server:', e);
        }
    }
    
    // Get error log
    getErrorLog() {
        return this.errorLog;
    }
    
    // Clear error log
    clearErrorLog() {
        this.errorLog = [];
        try {
            sessionStorage.removeItem('errorLog');
        } catch (e) {
            console.error('Failed to clear error log:', e);
        }
    }
    
    // Create wrapped function with error handling
    wrapAsync(fn, context = '') {
        return async (...args) => {
            try {
                return await fn(...args);
            } catch (error) {
                this.handleError(error, context);
                throw error;
            }
        };
    }
}

// Create singleton instance
const errorHandler = new ErrorHandler();

// Add CSS for error notifications
const style = document.createElement('style');
style.textContent = `
    @keyframes slideIn {
        from {
            transform: translateX(100%);
            opacity: 0;
        }
        to {
            transform: translateX(0);
            opacity: 1;
        }
    }
    
    @keyframes slideOut {
        from {
            transform: translateX(0);
            opacity: 1;
        }
        to {
            transform: translateX(100%);
            opacity: 0;
        }
    }
`;
document.head.appendChild(style);

// Export for use in other modules
export default errorHandler;